import { ErrorListProps, RJSFValidationError } from '@rjsf/utils';
import { Alert, AlertTitle, Box, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { createPortal } from 'react-dom';
import { formatSchemaName } from '../../utils/formatSchemaName';

export const ErrorListTemplate = (props: ErrorListProps) => {
  const { errors, registry } = props;
  const { formContext } = registry;

  const scrollToField = (error: RJSFValidationError) => {
    // RJSF default ID generation logic approximation
    // property: ".firstName" -> id: "root_firstName"
    // property: ".items[0].name" -> id: "root_items_0_name"
    
    let fieldId = error.property!;
    if (fieldId.startsWith('.')) {
      fieldId = fieldId.substring(1);
    }
    
    // Replace brackets and dots with underscores
    fieldId = fieldId
      .replace(/\./g, '_')
      .replace(/\[/g, '_')
      .replace(/\]/g, '');
      
    const elementId = `root_${fieldId}`;
    const element = document.getElementById(elementId);
    
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    } else {
      console.warn(`Could not find element with id: ${elementId}`);
    }
  };

  if (!errors || errors.length === 0) {
    return null;
  }

  const getErrorText = (error: RJSFValidationError) => {
    // Customize error message
    const property = error.property?.split(".").pop();
    const formattedProperty = property ? formatSchemaName(property) : 'Field';

    const errorText = formattedProperty + ": " + error.message + ".";
    return errorText;
  }

  const content = (
    <Box mb={2}>
      <Alert severity="error">
        <AlertTitle>Please correct the following errors before continuing:</AlertTitle>
        <List dense sx={{ 
            mt: 0, 
            pt: 0,
            maxHeight: '300px',
            overflowY: 'auto',
            overflowX: 'hidden',
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            }
        }}>
          {errors.map((error: RJSFValidationError, i: number) => (
            <ListItem key={i} disablePadding>
              <ListItemButton 
                onClick={() => scrollToField(error)}
                sx={{ 
                    py: 1, 
                    px: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    borderBottom: '1px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        borderColor: 'rgba(211, 47, 47, 0.2)',
                        transform: 'translateX(4px)',
                    }
                }}
              >
                <ListItemText 
                    primary={getErrorText(error)} 
                    primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { cursor: 'pointer' }
                    }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Alert>
    </Box>
  );

  if (formContext && formContext.sidebarErrorContainer) {
    return createPortal(content, formContext.sidebarErrorContainer);
  }

  return content;
}